/**
 * `ProjectStore` — the abstraction the editor talks to instead of
 * touching localStorage or fetching the API directly.
 *
 * Two implementations live alongside this file: `localStorageStore`
 * (free mode, browser-local) and `createApiStore({ ... })` (paid
 * mode, cloud-backed). Switching between them is a single factory
 * call at app boot — the editor never branches on which mode it is.
 *
 * The interface is async even for the local store. Promises are zero
 * cost when the underlying read is sync, but they'd be expensive to
 * retrofit if we made the interface sync first and then needed to
 * swap in an HTTP backend.
 */

import type { StoredProject } from '../projects.js';

/**
 * Project shape returned by the store. Inherits the free-mode
 * `StoredProject` shape and adds optional cloud-only fields. The
 * editor reads `schema`/`data`/etc. uniformly — cloud-only fields
 * are picked up only by the bits that need them (the deploy button,
 * the save flow's optimistic-concurrency token).
 */
export interface Project extends StoredProject {
  /** Cloud only. Monotonic per-project version for optimistic concurrency. */
  version?: number;
  /** Cloud only. Reference to an org-scoped deploy target. */
  deployTargetId?: string | null;
  /** Cloud only. Reference to an org-scoped asset origin. */
  assetOriginId?: string | null;
}

/**
 * The signed-in user's effective capabilities on a project. Computed
 * server-side from the role matrix (see @bliksys/db) and returned on
 * project reads; the editor uses it to hide/disable actions. Free mode
 * is single-user and local, so everything is permitted (FULL_ACCESS).
 */
export interface ProjectCapabilities {
  canRead: boolean;
  canEditContent: boolean;
  canEditSchema: boolean;
  canConfigure: boolean;
  canDeploy: boolean;
  canManageMembers: boolean;
}

/** Default for free mode and as a safe fallback before a fetch resolves. */
export const FULL_ACCESS: ProjectCapabilities = {
  canRead: true,
  canEditContent: true,
  canEditSchema: true,
  canConfigure: true,
  canDeploy: true,
  canManageMembers: true,
};

/** Lightweight row used by listing views. Matches the server's list shape. */
export interface ProjectSummary {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  /** Free mode: kind from the inline deployTarget. Cloud mode: filled
   * from the deploy-target lookup keyed by `deployTargetId`. */
  deployTargetKind?: 'github' | 'webhook' | 'cloudflareKV' | undefined;
}

export interface ProjectStore {
  /** Tag identifying which backend this store is. Cheap to read; used
   * by the few UI bits that genuinely differ (e.g. ProjectSettings'
   * inline-secrets form vs. cloud's "pick a saved target" picker). */
  readonly mode: 'free' | 'cloud';

  /** Cloud-mode only — the active organization slug. Free stores leave
   * it undefined. Surfaced here so the deploy handler can pass it to
   * the paid endpoint without going back to the auth context. */
  readonly orgSlug?: string;

  list(): Promise<ProjectSummary[]>;
  get(id: string): Promise<Project | null>;
  /** Save returns the stored Project (with server-side fields filled
   * in for cloud mode — version bumped, updatedAt refreshed). */
  save(project: Project): Promise<Project>;
  delete(id: string): Promise<void>;
  /** Cloud only — the caller's capabilities on a project. Free stores omit
   * it; callers treat its absence as FULL_ACCESS (single-user local mode). */
  getCapabilities?(id: string): Promise<ProjectCapabilities>;
}

/**
 * Thrown by `ApiStore.save()` when the server rejects the write because
 * the project has been edited elsewhere since `project.version` was
 * read. The caller (editor save flow) reloads, replays the user's
 * local edits, and tries again.
 */
export class VersionConflictError extends Error {
  readonly actualVersion: number;
  readonly expectedVersion: number;
  constructor(message: string, args: { actualVersion: number; expectedVersion: number }) {
    super(message);
    this.name = 'VersionConflictError';
    this.actualVersion = args.actualVersion;
    this.expectedVersion = args.expectedVersion;
  }
}

/**
 * Thrown when the user isn't (or is no longer) signed in. The editor
 * surfaces this by sending the user to the cloud sign-in page with
 * a `?return_to=` pointing back to the current project URL.
 */
export class UnauthenticatedError extends Error {
  constructor(message = 'Sign in required') {
    super(message);
    this.name = 'UnauthenticatedError';
  }
}
